/**
 * Trophy3D — real-WebGL rotating gold trophy. Used on the TV (Scene 2 KOTM
 * peak) and on the GoalMountain peak. Self-contained; just drop it inside any
 * sized container and it fills it.
 *
 * The Canvas has a transparent background so the trophy floats over whatever
 * is behind it. Camera is pulled back so handles swing without clipping the
 * canvas edge as the cup rotates.
 */
/* eslint-disable react/no-unknown-property -- three.js / r3f use lowercase JSX props */
import React, { Suspense, useRef } from "react";
import Box from "@mui/material/Box";
import { Canvas, useFrame } from "@react-three/fiber";

const TrophyMesh = () => {
  const groupRef = useRef();
  useFrame((_, delta) => {
    if (groupRef.current) groupRef.current.rotation.y += delta * 0.7;
  });

  const goldMaterial = (
    <meshStandardMaterial color="#F2B83A" metalness={0.85} roughness={0.22} />
  );

  return (
    <group ref={groupRef} position={[0, -0.05, 0]}>
      {/* Cup bowl */}
      <mesh position={[0, 0.5, 0]}>
        <cylinderGeometry args={[0.55, 0.32, 0.7, 48]} />
        {goldMaterial}
      </mesh>
      {/* Rim */}
      <mesh position={[0, 0.85, 0]}>
        <torusGeometry args={[0.55, 0.04, 16, 48]} />
        {goldMaterial}
      </mesh>
      {/* Bowl bottom (rounded) */}
      <mesh position={[0, 0.16, 0]}>
        <sphereGeometry args={[0.32, 32, 24, 0, Math.PI * 2, 0, Math.PI / 2]} />
        {goldMaterial}
      </mesh>
      {/* Stem */}
      <mesh position={[0, -0.05, 0]}>
        <cylinderGeometry args={[0.08, 0.1, 0.3, 24]} />
        {goldMaterial}
      </mesh>
      {/* Stem knob */}
      <mesh position={[0, -0.1, 0]}>
        <sphereGeometry args={[0.13, 24, 18]} />
        {goldMaterial}
      </mesh>
      {/* Base */}
      <mesh position={[0, -0.32, 0]}>
        <cylinderGeometry args={[0.42, 0.46, 0.18, 48]} />
        {goldMaterial}
      </mesh>
      {/* Base ring */}
      <mesh position={[0, -0.24, 0]}>
        <torusGeometry args={[0.42, 0.025, 12, 48]} />
        {goldMaterial}
      </mesh>
      {/* Left handle */}
      <mesh position={[-0.6, 0.5, 0]} rotation={[0, 0, Math.PI / 2]}>
        <torusGeometry args={[0.18, 0.045, 16, 32, Math.PI]} />
        {goldMaterial}
      </mesh>
      {/* Right handle */}
      <mesh position={[0.6, 0.5, 0]} rotation={[0, 0, -Math.PI / 2]}>
        <torusGeometry args={[0.18, 0.045, 16, 32, Math.PI]} />
        {goldMaterial}
      </mesh>
      {/* Star on cup front */}
      <mesh position={[0, 0.5, 0.51]}>
        <sphereGeometry args={[0.09, 12, 12]} />
        <meshStandardMaterial
          color="#FFF1B0"
          metalness={0.95}
          roughness={0.15}
          emissive="#FFD27A"
          emissiveIntensity={0.4}
        />
      </mesh>
    </group>
  );
};

const Trophy3D = ({ size = "6vh" }) => (
  <Box
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      pointerEvents: "none",
    }}
  >
    <Canvas
      camera={{ position: [0, 0.3, 4.2], fov: 24 }}
      dpr={[1, 2]}
      gl={{ antialias: true, alpha: true }}
      style={{ background: "transparent" }}
    >
      <ambientLight intensity={0.55} color="#FFF7D6" />
      <directionalLight position={[3, 5, 3]} intensity={1.4} color="#FFF7D6" />
      <directionalLight position={[-3, 2, 2]} intensity={0.6} color="#FFD27A" />
      <pointLight position={[0, 1.5, 2]} intensity={0.6} color="#FFFFFF" />
      <Suspense fallback={null}>
        <TrophyMesh />
      </Suspense>
    </Canvas>
  </Box>
);

/* eslint-enable react/no-unknown-property */

export default Trophy3D;
